/**
 * wayai diff [--hub <id|name>] [--production] — dry-run diff of local files vs the platform
 *
 * Uses the same diff engine + renderer as push/pull (entities matched by name),
 * so it is meaningful even though a production hub's entity UUIDs are remapped.
 *   - default:      local files → live PREVIEW state
 *   - --production: local preview files → live PRODUCTION state (the linked hub)
 *
 * Read-only: never writes files, never pushes. The diff route has no production
 * guard, so `--production` works against the linked production hub with a normal
 * `hub:read` grant.
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { parseHubFolder } from '../lib/parser.js';
import { pageDesignWarnings } from '../lib/page-design-lint.js';
import { printDiff } from '../lib/diff-display.js';
import { printWarnings } from './push.js';
import { resolveHubTarget } from './pull.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { warnLayoutOnce } from '../lib/layout.js';
import { findGitRoot, resolveWorkspaceDir } from '../lib/workspace.js';
import { assertResourcePayloadFitsWithoutPruning, prepareResourcePayload } from '../lib/resource-files.js';

interface ParsedArgs {
  production: boolean;
  hubSelector?: string;
}

export function parseArgs(args: string[]): ParsedArgs {
  let production = false;
  let hubSelector: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--production') {
      production = true;
    } else if (arg === '--hub' && args[i + 1]) {
      hubSelector = args[++i];
    }
  }

  return { production, hubSelector };
}

export async function diffCommand(args: string[]): Promise<void> {
  const { production, hubSelector } = parseArgs(args);
  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // Resolve the local hub folder (the preview the workspace operates on).
  const workspaceDir = resolveWorkspaceDir();
  const gitRoot = findGitRoot();
  if (gitRoot) warnLayoutOnce(gitRoot);

  const { hubId: previewHubId, hubFolder } = resolveHubTarget(workspaceDir, hubSelector);
  if (!hubFolder) {
    console.error('Hub is not materialized locally — nothing to diff. Run `wayai pull` first.');
    process.exit(1);
  }

  console.log('Parsing local configuration...');
  const localConfig = parseHubFolder(hubFolder);

  // Resolve the diff target hub_id: the preview itself, or its linked production.
  let targetHubId = previewHubId;
  let label = 'preview';
  if (production) {
    // The resolved hub is itself a production mirror (reached via cwd or an
    // explicit --hub) — there's no parent to diff against. Distinguish this from
    // an unpublished preview so the message isn't misleading.
    if (localConfig.hub_environment === 'production') {
      console.log('This is a read-only production mirror — run `wayai diff --production` from the preview workspace instead.');
      return;
    }
    const { hubs } = await client.listHubs(organizationId);
    const entry = hubs.find((h) => h.hub_id === previewHubId);
    const productionHubId = entry?.production_hub_id ?? null;
    if (!productionHubId) {
      console.log('This preview has no linked production hub yet (never published) — nothing to diff against production.');
      return;
    }
    targetHubId = productionHubId;
    label = 'production';
  }

  console.log(`Computing diff against ${label}...`);
  let diffPayload = localConfig;
  try {
    diffPayload = prepareResourcePayload(localConfig, await client.pull(targetHubId, organizationId));
  } catch (err) {
    assertResourcePayloadFitsWithoutPruning(localConfig, err);
  }
  const result = await client.diff(targetHubId, diffPayload, 'push', organizationId);

  printWarnings([...result.warnings, ...pageDesignWarnings(localConfig.pages)]);

  if (!result.has_changes) {
    console.log(`\nNo differences — local files match ${label}.`);
    return;
  }

  console.log(`\nLocal files vs ${label.toUpperCase()}:`);
  printDiff(result.summary);
}
